import React, { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useProducts } from 'contexts/ProductsContext';
import { useCart } from 'contexts/CartContext';
import { useSizePreference } from 'contexts/SizePreference';
import { getColorHex, sortColors } from 'lib/colors';
import MobileProductView from './MobileProductView';
import MobileCart from './MobileCart';
import logoMain from 'assets/logo_main.png';
import musicbg from 'assets/bgs/musicbg.jpg';
import gamesbg from 'assets/bgs/gamesbg.jpg';
import memesbg from 'assets/bgs/memesbg.jpg';
import mm0bg from 'assets/bgs/mm0bg.jpg';
import mm1bg from 'assets/bgs/mm1bg.jpg';
import mm2bg from 'assets/bgs/mm2bg.jpg';
import mm3bg from 'assets/bgs/mm3bg.jpg';
import overlaygrit from 'assets/bgs/overlaygrit1.jpg';
import logosmix from 'assets/logosmix.png';
import carousel1 from 'assets/carousel/herewego.jpg';
import carousel2 from 'assets/carousel/balls.jpg';
import carousel3 from 'assets/carousel/boonk.jpg';
import carousel4 from 'assets/carousel/granny.jpg';
import carousel5 from 'assets/carousel/princess.jpg';
import carousel6 from 'assets/carousel/rookie.jpg';

const carouselItems = [
  { img: carousel1, productName: 'Mike Wazowski' },
  { img: carousel2, productName: 'Mister Balls' },
  { img: carousel3, productName: 'Cursed Emoji' },
  { img: carousel4, productName: 'Minesweeper cat' },
  { img: carousel5, productName: 'Evil kitty princess' },
  { img: carousel6, productName: 'Halo 3 ODST' },
];
const carouselImages = carouselItems.map(c => c.img);

const homeBgs = [mm0bg, mm1bg, mm2bg, mm3bg];
const categoryBgs = { music: musicbg, games: gamesbg, goofy: memesbg };

function getBrandName(product) {
  if (typeof product === 'string') return product.replace(/\s*Oversized Tee$/i, '').trim();
  if (product.band) return product.band;
  if (product.game) return product.game;
  if (product.brand) return product.brand;
  return product.name.replace(/\s*Oversized Tee$/i, '').trim();
}

function MobileFeaturedSlideshow({ products, onClick, activeColor }) {
  const [currentPage, setCurrentPage] = React.useState(0);
  const [progress, setProgress] = React.useState(0);
  const [isSliding, setIsSliding] = React.useState(false);
  const [slideDir, setSlideDir] = React.useState('left');
  const [paused, setPaused] = React.useState(false);
  const touchRef = React.useRef({ startX: 0, startTime: 0 });
  const itemsPerPage = 2;
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const needsSlideshow = totalPages > 1;
  const safePage = currentPage % totalPages;

  React.useEffect(() => {
    if (!needsSlideshow || paused) return;
    const interval = 50;
    const duration = 4000;
    const step = (interval / duration) * 100;
    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          setSlideDir('left');
          setIsSliding(true);
          setTimeout(() => {
            setCurrentPage(prev => (prev + 1) % totalPages);
            setIsSliding(false);
          }, 400);
          return 0;
        }
        return p + step;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [totalPages, needsSlideshow, paused]);

  const nextPage = (safePage + 1) % totalPages;
  const prevPage = (safePage - 1 + totalPages) % totalPages;

  function slideNext() {
    if (isSliding) return;
    setSlideDir('left');
    setIsSliding(true);
    setProgress(0);
    setTimeout(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
      setIsSliding(false);
    }, 400);
  }

  function slidePrev() {
    if (isSliding) return;
    setSlideDir('right');
    setProgress(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsSliding(true);
        setTimeout(() => {
          setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
          setIsSliding(false);
        }, 400);
      });
    });
  }

  function goToPage(i) {
    if (i === safePage || isSliding) return;
    const dir = i > safePage ? 'left' : 'right';
    setSlideDir(dir);
    setProgress(0);
    if (dir === 'right') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsSliding(true);
          setTimeout(() => { setCurrentPage(i); setIsSliding(false); }, 400);
        });
      });
    } else {
      setIsSliding(true);
      setTimeout(() => { setCurrentPage(i); setIsSliding(false); }, 400);
    }
  }

  function onTouchStart(e) {
    touchRef.current = { startX: e.touches[0].clientX, startTime: Date.now() };
    setPaused(true);
  }
  function onTouchEnd(e) {
    setPaused(false);
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dt = Date.now() - touchRef.current.startTime;
    if (Math.abs(dx) > 40 && dt < 500) {
      if (dx < 0) slideNext();
      else slidePrev();
    }
  }

  const adjacentPage = slideDir === 'left' ? nextPage : prevPage;
  const sliderClass = `m-featured-slide__slider${isSliding ? (slideDir === 'left' ? ' m-featured-slide__slider--slide-left' : ' m-featured-slide__slider--slide-right') : ''}`;

  const renderPage = (pageIdx) =>
    products.slice(pageIdx * itemsPerPage, pageIdx * itemsPerPage + itemsPerPage).map(product => (
      <MobileProductCard key={product.id} product={product} onClick={() => onClick(product)} activeColor={activeColor} />
    ));

  return (
    <div className="m-featured-slide" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {needsSlideshow && <button className="m-featured-slide__arrow m-featured-slide__arrow--left" onClick={slidePrev}>◀</button>}
      {needsSlideshow && <button className="m-featured-slide__arrow m-featured-slide__arrow--right" onClick={slideNext}>▶</button>}
      <div className={sliderClass} style={slideDir === 'right' && !isSliding ? { transform: 'translateX(-50%)' } : {}}>
        {slideDir === 'right' ? (
          <>
            <div className="m-featured-slide__page">{renderPage(adjacentPage)}</div>
            <div className="m-featured-slide__page">{renderPage(safePage)}</div>
          </>
        ) : (
          <>
            <div className="m-featured-slide__page">{renderPage(safePage)}</div>
            <div className="m-featured-slide__page">{renderPage(adjacentPage)}</div>
          </>
        )}
      </div>
      {needsSlideshow && (
        <div className="m-featured-slide__indicators">
          {Array.from({ length: totalPages }).map((_, i) => (
            <div
              key={i}
              className="m-featured-slide__dash"
              onClick={() => goToPage(i)}
            >
              <div
                className="m-featured-slide__dash-fill"
                style={{ width: i === safePage ? `${progress}%` : i < safePage ? '100%' : '0%' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileCarousel({ images, onClickSlide }) {
  const trackRef = React.useRef(null);
  const [offset, setOffset] = React.useState(0);
  const [firstHalfWidth, setFirstHalfWidth] = React.useState(0);
  const [loadedCount, setLoadedCount] = React.useState(0);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || loadedCount < images.length) return;
    const slides = track.querySelectorAll('.mc-slide');
    let w = 0;
    for (let i = 0; i < images.length && i < slides.length; i++) {
      w += slides[i].offsetWidth;
    }
    if (w > 0) setFirstHalfWidth(w);
  }, [images, loadedCount]);

  React.useEffect(() => {
    if (!firstHalfWidth) return;
    let raf;
    let last = performance.now();
    let pos = offset;
    function tick(now) {
      const dt = (now - last) / 1000;
      last = now;
      pos -= 35 * dt;
      if (pos <= -firstHalfWidth) pos += firstHalfWidth;
      setOffset(pos);
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [firstHalfWidth]);

  return (
    <div className="m-carousel">
      <div ref={trackRef} className="m-carousel__track" style={{ transform: `translateX(${offset}px)` }}>
        {[...images, ...images, ...images].map((img, i) => (
          <div key={i} className="mc-slide m-carousel__slide" onClick={() => onClickSlide && onClickSlide(i % images.length)} style={{ cursor: onClickSlide ? 'pointer' : 'default' }}>
            <img src={img} alt="" onLoad={i < images.length ? () => setLoadedCount(c => c + 1) : undefined} />
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileProductCard({ product, onClick, activeColor }) {
  // When a single colour is filtered (and the product has it), show that colour's images.
  const c = activeColor && product.colors && product.colors.includes(activeColor) ? activeColor : null;
  const frontImg = (c && product.colorImages && product.colorImages[c] && product.colorImages[c][0]) || product.images[0];
  const backColor = c || product.defaultColor;
  const backImg = product.isDualPrint && backColor
    ? ((product.colorBackImages && product.colorBackImages[backColor]) || (product.colorImages && product.colorImages[backColor]?.[1]))
    : null;

  return (
    <div className="m-card" onClick={onClick}>
      <div className={`m-card__img ${backImg ? 'm-card__img--dual' : ''}`}>
        <img className="m-card__img-front" src={frontImg} alt={product.name} />
        {backImg && <img className="m-card__img-back" src={backImg} alt={product.name + ' back'} />}
      </div>
      <div className="m-card__name">{product.name}</div>
      <div className="m-card__price">₹{product.price.toLocaleString()}</div>
    </div>
  );
}

function MobileSearchOverlay({ searchQuery, setSearchQuery, products, onSelect, onSelectBrand, onClose, bgImage }) {
  const q = searchQuery.toLowerCase();
  const catLabel = (cat) => cat === 'music' ? 'BANDS' : cat.toUpperCase();

  const brandMatches = [];
  const seen = new Set();
  products.forEach(p => {
    if (p.category === 'goofy') return;
    const brand = getBrandName(p);
    if (brand.toLowerCase().includes(q) && !seen.has(brand)) {
      seen.add(brand);
      const count = products.filter(pp => getBrandName(pp) === brand).length;
      brandMatches.push({ brand, category: p.category, count });
    }
  });
  const productMatches = products.filter(p => p.name.toLowerCase().includes(q));

  return (
    <div className="m-search-fulloverlay" onClick={(e) => { if (e.target === e.currentTarget || e.target.classList.contains('m-search-fulloverlay__scroll')) onClose(); }}>
      <div className="m-search-fulloverlay__bg" style={{ backgroundImage: `url(${bgImage})` }} />
      <div className="m-search-fulloverlay__scroll">
        {q.length > 0 ? (
          <>
            {brandMatches.length > 0 && (
              <>
                <div className="m-search-fulloverlay__section">BRANDS / GAMES</div>
                <div className="m-search-fulloverlay__brands">
                  {brandMatches.map(({ brand, category, count }) => (
                    <button key={`brand-${brand}`} className="m-search-fulloverlay__brand-tag" onClick={() => onSelectBrand(brand, category)}>
                      {brand} ({count}) <span>{catLabel(category)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            {productMatches.length > 0 && (
              <>
                <div className="m-search-fulloverlay__section">PRODUCTS ({productMatches.length})</div>
                <div className="m-grid">
                  {productMatches.map(product => (
                    <MobileProductCard key={product.id} product={product} onClick={() => onSelect(product)} />
                  ))}
                </div>
              </>
            )}
            {brandMatches.length === 0 && productMatches.length === 0 && (
              <div className="m-search-fulloverlay__empty">No results found</div>
            )}
          </>
        ) : (
          <div className="m-search-fulloverlay__empty">Start typing to search</div>
        )}
      </div>
    </div>
  );
}

function MobileBinco({ sharedProduct, sharedCategory, sharedBrand }) {
  const { products, loading } = useProducts();
  const { getItemCount } = useCart();
  const { sizePreference, setSizePreference } = useSizePreference();
  const [tab, setTab] = useState('home'); // 'home', 'categories', 'cart'
  const [activeCategory, setActiveCategory] = useState(sharedCategory || null); // null, 'music', 'games', 'goofy'
  const [expandedCat, setExpandedCat] = useState(null);
  const [searchQuery, setSearchQuery] = useState(sharedBrand || '');
  const [viewMode, setViewMode] = useState('grid');
  const [viewingProduct, setViewingProduct] = useState(null);
  const [productSelections, setProductSelections] = useState({}); // { color, size }
  const [dialogMsg, setDialogMsg] = useState(null);
  const [colorFilters, setColorFilters] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const availableColors = useMemo(
    () => sortColors([...new Set(products.flatMap(p => p.colors || []))]),
    [products],
  );
  // When exactly one colour is filtered, use it for thumbnails and product pre-selection.
  const activeColor = colorFilters.length === 1 ? colorFilters[0] : null;

  const homeBg = useMemo(() => homeBgs[Math.floor(Math.random() * homeBgs.length)], []);
  const [searchOpen, setSearchOpen] = useState(false);

  // Auto-open shared product from URL
  useEffect(() => {
    if (sharedProduct && !viewingProduct) {
      setViewingProduct(sharedProduct);
    }
  }, [sharedProduct]);

  // Browser back button support
  const pushState = useCallback((state) => {
    window.history.pushState(state, '');
  }, []);

  useEffect(() => {
    // Push initial state
    window.history.replaceState({ view: 'home' }, '');

    function handlePopState(e) {
      const state = e.state;
      if (!state) {
        // No more history — push home state to prevent exiting
        window.history.pushState({ view: 'home' }, '');
        setViewingProduct(null);
        setTab('home');
        setActiveCategory(null);
        return;
      }
      if (state.view === 'home') {
        setViewingProduct(null);
        setTab('home');
        setActiveCategory(null);
        setSearchOpen(false);
        setSearchQuery('');
      } else if (state.view === 'categories') {
        setViewingProduct(null);
        setTab('categories');
        setActiveCategory(null);
        setSearchOpen(false);
        setSearchQuery('');
      } else if (state.view === 'category') {
        setViewingProduct(null);
        setTab('home');
        setActiveCategory(state.category);
        setSearchQuery(state.brandFilter || '');
      } else if (state.view === 'product') {
        const p = products.find(pr => pr.id === state.productId);
        if (p) {
          setViewingProduct(p);
          setProductSelections(state.selections || {});
          setTab('home');
          setSearchQuery('');
        } else {
          setViewingProduct(null);
        }
      } else if (state.view === 'cart') {
        setViewingProduct(null);
        setTab('cart');
      } else if (state.view === 'search') {
        setViewingProduct(null);
        setActiveCategory(null);
        setSearchOpen(true);
        setSearchQuery(state.query || '');
        setTab('home');
      }
    }

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [products]);

  // Navigation with clean history — replace instead of push when staying in same level
  function navigateTab(t) {
    if (t === 'home') {
      pushState({ view: 'home' });
      setTab('home');
      setActiveCategory(null);
      setSearchQuery('');
    } else if (t === 'categories') {
      pushState({ view: 'categories' });
      setTab('categories');
      setActiveCategory(null);
    } else if (t === 'cart') {
      pushState({ view: 'cart' });
      setTab('cart');
    }
  }

  function navigateCategory(cat) {
    pushState({ view: 'category', category: cat });
    setActiveCategory(cat);
    setTab('home');
    setSearchQuery('');
  }

  function navigateProduct(product, selections) {
    let sel = selections || {};
    // When exactly one colour is filtered, pre-select it on the opened product.
    if (!sel.color && colorFilters.length === 1 && product.colors && product.colors.includes(colorFilters[0])) {
      sel = { ...sel, color: colorFilters[0] };
    }
    pushState({ view: 'product', productId: product.id, selections: sel });
    setViewingProduct(product);
    setProductSelections(sel);
  }

  function navigateBack() {
    window.history.back();
  }

  // Filter products
  const filtered = products.filter(p => {
    const matchCat = !activeCategory || (p.categories ? p.categories.includes(activeCategory) : p.category === activeCategory);
    const q = searchQuery.toLowerCase();
    const matchSearch = p.name.toLowerCase().includes(q) || getBrandName(p).toLowerCase().includes(q);
    const matchColor = colorFilters.length === 0 || (p.colors && p.colors.some(c => colorFilters.includes(c)));
    return matchCat && matchSearch && matchColor;
  });

  const sorted = [...filtered]
    .map(p => ({ ...p, brand: getBrandName(p) }))
    .sort((a, b) => a.brand.localeCompare(b.brand));

  // Group by letter
  const grouped = {};
  sorted.forEach(p => {
    const letter = p.brand[0].toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(p);
  });
  const activeLetters = Object.keys(grouped).sort();

  const currentBg = !activeCategory ? homeBg : categoryBgs[activeCategory] || null;

  function onShowDialog(type, msg) {
    setDialogMsg({ type, msg });
  }

  // Wrap everything so dialog is always on top
  return (
    <>
      {/* Dialog overlay - always rendered on top of everything */}
      {dialogMsg && (
        <DialogOverlay onClick={() => setDialogMsg(null)}>
          <div className="m-dialog" onClick={e => e.stopPropagation()}>
            <div className={`m-dialog__icon ${dialogMsg.type === 'error' ? 'm-dialog__icon--error' : ''}`}>
              {dialogMsg.type === 'error' ? '⚠' : '✓'}
            </div>
            <p>{dialogMsg.msg}</p>
            <button onClick={() => setDialogMsg(null)}>OK</button>
          </div>
        </DialogOverlay>
      )}

      {viewingProduct ? (
        <Container>
          {/* Top bar stays visible */}
          <header className="m-header">
            <img className="m-header__logo" src={logoMain} alt="binco" onClick={() => { setViewingProduct(null); navigateTab('home'); setSearchQuery(''); setColorFilters([]); }} />
          </header>
          <MobileProductView
            key={`${viewingProduct.id}-${productSelections.color}-${productSelections.size}`}
            product={viewingProduct}
            initialSelections={productSelections}
            onBack={navigateBack}
            onShowDialog={onShowDialog}
            onViewProduct={(p) => navigateProduct(p)}
            onOpenCategory={(cat, brand) => {
              setViewingProduct(null);
              navigateCategory(cat);
              if (brand) setSearchQuery(brand);
            }}
            onOpenCart={(selections) => {
              // Replace current product history entry with selections preserved
              window.history.replaceState(
                { view: 'product', productId: viewingProduct.id, selections: selections || {} },
                ''
              );
              pushState({ view: 'cart' });
              setTab('cart');
              setViewingProduct(null);
            }}
          />
          <div className="m-payment-badges">
            <img src={logosmix} alt="Powered by Shopify & Razorpay" />
          </div>
          <BottomNav
            tab={tab}
            setTab={(t) => { setViewingProduct(null); navigateTab(t); }}
            cartCount={getItemCount()}
          />
        </Container>
      ) : tab === 'cart' ? (
        <Container>
          <MobileCart
            onBack={navigateBack}
            onShowDialog={onShowDialog}
            onViewProduct={(p, sel) => navigateProduct(p, sel)}
          />
          <div className="m-payment-badges">
            <img src={logosmix} alt="Powered by Shopify & Razorpay" />
          </div>
          <BottomNav tab={tab} setTab={navigateTab} cartCount={getItemCount()} />
        </Container>
      ) : (
    <Container>
      {/* Top bar */}
      <header className="m-header">
        <img className="m-header__logo" src={logoMain} alt="binco" onClick={() => { setViewingProduct(null); navigateTab('home'); setSearchQuery(''); setColorFilters([]); }} />
        <div className={`m-header__search ${searchOpen ? 'm-header__search--expanded' : ''}`}>
          <input
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => { if (!searchOpen) { pushState({ view: 'search', query: '' }); setSearchOpen(true); } }}
          />
          {searchOpen && (
            <button className="m-search-close" onClick={() => { setSearchOpen(false); setSearchQuery(''); window.history.back(); }}>✕</button>
          )}
        </div>
        <button
          className={`m-header__filterbtn ${(colorFilters.length > 0 || sizePreference) ? 'm-active' : ''}`}
          onClick={() => setFiltersOpen(o => !o)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          {colorFilters.length > 0 && <span className="m-header__filterbadge">{colorFilters.length}</span>}
        </button>
        <button
          className="m-header__viewbtn m-active"
          onClick={() => window.open('https://shopify.com/99290480957/account/profile', '_blank')}
        ><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></button>
      </header>

      {/* Filters panel */}
      {filtersOpen && (
        <div className="m-filters-backdrop" onClick={() => setFiltersOpen(false)}>
          <div className="m-filters-panel" onClick={e => e.stopPropagation()}>
            <div className="m-filters__bar">
              <span className="m-filters__title">Filters</span>
              <button className="m-filters__done" onClick={() => setFiltersOpen(false)}>Done</button>
            </div>

            <div className="m-filters__section">
              <div className="m-filters__heading">My Size</div>
              <div className="m-filters__sizes">
                <button
                  className={`m-filters__chip ${sizePreference === '' ? 'm-filters__chip--active' : ''}`}
                  onClick={() => setSizePreference('')}
                >Any</button>
                {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'].map(s => (
                  <button
                    key={s}
                    className={`m-filters__chip ${sizePreference === s ? 'm-filters__chip--active' : ''}`}
                    onClick={() => setSizePreference(s)}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div className="m-filters__section">
              <div className="m-filters__heading">
                Colour
                {colorFilters.length > 0 && (
                  <button className="m-filters__clear" onClick={() => setColorFilters([])}>Clear ({colorFilters.length})</button>
                )}
              </div>
              <div className="m-filters__colors">
                {availableColors.map(c => {
                  const active = colorFilters.includes(c);
                  return (
                    <button
                      key={c}
                      className={`m-filters__color ${active ? 'm-filters__color--active' : ''}`}
                      onClick={() => setColorFilters(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                    >
                      <span className="m-filters__swatch" style={{ background: getColorHex(c) }} />
                      <span className="m-filters__colorname">{c}</span>
                      {active && <span className="m-filters__check">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search overlay */}
      {searchOpen && (
        <MobileSearchOverlay
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          products={products}
          bgImage={homeBg}
          onSelect={p => {
            // Save search state so back returns here
            window.history.replaceState({ view: 'search', query: searchQuery }, '');
            setSearchOpen(false);
            navigateProduct(p);
          }}
          onSelectBrand={(brand, cat) => {
            // Save search state so back returns here
            window.history.replaceState({ view: 'search', query: searchQuery }, '');
            setSearchOpen(false);
            // Navigate to category with brand filter preserved in history
            pushState({ view: 'category', category: cat, brandFilter: brand });
            setActiveCategory(cat);
            setTab('home');
            setSearchQuery(brand);
          }}
          onClose={() => { setSearchOpen(false); setSearchQuery(''); window.history.back(); }}
        />
      )}

      {/* Category header if in a category */}
      {activeCategory && (
        <div className="m-cat-header">
          <button onClick={() => setActiveCategory(null)}>← All</button>
          <span>{activeCategory.toUpperCase()}</span>
          <span className="m-cat-count">{filtered.length}</span>
        </div>
      )}

      {/* Content */}
      <main
        className="m-content"
        style={currentBg ? { backgroundImage: `url(${currentBg})` } : {}}
      >
        {loading ? (
          <div className="m-loading">
            <div className="m-loading__bar"><div className="m-loading__fill" /></div>
            <div className="m-loading__text">Loading products...</div>
          </div>
        ) : tab === 'categories' && !activeCategory ? (
          /* Category list */
          <div className="m-categories">
            <div className="m-categories__heading">Browse Tshirts by Category</div>
            {['music', 'games', 'goofy'].map(cat => {
              const catProducts = products
                .filter(p => p.categories ? p.categories.includes(cat) : p.category === cat)
                .map(p => ({ ...p, brand: getBrandName(p) }))
                .sort((a, b) => a.brand.localeCompare(b.brand));
              const isExpanded = expandedCat === cat;
              const uniqueBrands = [...new Set(catProducts.map(p => p.brand))];
              // For music, group by genre
              const genres = cat === 'music'
                ? [...new Set(catProducts.filter(p => p.genre).map(p => p.genre))].sort()
                : [];

              const hasSubcategories = catProducts.some(p => p.band || p.game);

              return (
                <div key={cat} className="m-cat-group">
                  <button
                    className="m-cat-btn"
                    onClick={() => hasSubcategories ? setExpandedCat(isExpanded ? null : cat) : navigateCategory(cat)}
                  >
                    <span>{cat === 'music' ? 'BANDS' : cat.toUpperCase()}</span>
                    {hasSubcategories && <span className="m-cat-arrow">{isExpanded ? '▼' : '▶'}</span>}
                  </button>
                  {isExpanded && (
                    <div className="m-cat-expanded">
                      <button
                        className="m-cat-all"
                        onClick={() => navigateCategory(cat)}
                      >
                        View All {cat === 'music' ? 'BANDS' : cat.toUpperCase()} →
                      </button>
                      <div className="m-cat-brands">
                        {uniqueBrands.map(brand => {
                          const count = catProducts.filter(p => p.brand === brand).length;
                          return (
                          <button key={brand} className="m-cat-sub" onClick={() => {
                            navigateCategory(cat);
                            setSearchQuery(brand);
                          }}>
                            {brand}{` (${count})`}
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Product grid */
          <>
            {!activeCategory && (
              <div className="m-banner">
                Free shipping above ₹999
              </div>
            )}
            {(() => {
              const featuredItems = !activeCategory
                ? sorted.filter(p => p.isFeaturedHome).sort((a, b) => a.featuredOrder - b.featuredOrder)
                : sorted.filter(p => p.isFeatured);
              const hasF = featuredItems.length > 0;
              return hasF ? (
                <>
                  <div className="m-alpha__label">★ Featured</div>
                  <MobileFeaturedSlideshow products={featuredItems} onClick={(p) => navigateProduct(p)} activeColor={activeColor} />
                  <div className="m-section-divider" />
                  <div className="m-alpha__label">All Products</div>
                </>
              ) : null;
            })()}
            {viewMode === 'grid' ? (
              <div className="m-grid">
                {sorted.map(product => (
                  <MobileProductCard key={product.id} product={product} onClick={() => navigateProduct(product)} activeColor={activeColor} />
                ))}
              </div>
            ) : (
              <div className="m-alpha">
                {activeLetters.map(letter => (
                  <div key={letter}>
                    <div className="m-alpha__label">{letter}</div>
                    <div className="m-grid">
                      {grouped[letter].map(product => (
                        <MobileProductCard key={product.id} product={product} onClick={() => navigateProduct(product)} activeColor={activeColor} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {sorted.length === 0 && <div className="m-empty">No products found</div>}
            {!activeCategory && (
              <MobileCarousel images={carouselImages} onClickSlide={(i) => {
                const item = carouselItems[i];
                if (item) {
                  const match = products.find(p => p.name.toLowerCase().includes(item.productName.toLowerCase()));
                  if (match) navigateProduct(match);
                }
              }} />
            )}
          </>
        )}
      </main>

      <div className="m-footer-strip">
        <div className="m-footer-strip__links">
          <a className="m-footer-strip__email" href="/policies/privacy-policy.html" target="_blank">Privacy</a>
          <span className="m-footer-strip__sep">|</span>
          <a className="m-footer-strip__email" href="/policies/faq.html" target="_blank">FAQs</a>
          <span className="m-footer-strip__sep">|</span>
          <a className="m-footer-strip__email" href="/policies/shipping-returns.html" target="_blank">Shipping</a>
          <span className="m-footer-strip__sep">|</span>
          <a className="m-footer-strip__icon" href="mailto:support@binco67.in" onClick={(e) => { e.preventDefault(); navigator.clipboard.writeText('support@binco67.in').then(() => { e.currentTarget.setAttribute('data-copied', '1'); setTimeout(() => e.currentTarget && e.currentTarget.removeAttribute('data-copied'), 1500); }).catch(() => {}); }} title="support@binco67.in">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </a>
          <a className="m-footer-strip__icon" href="https://www.instagram.com/binco67.in/" target="_blank" rel="noopener noreferrer" title="Instagram">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
        </div>
        <div className="m-payment-badges">
          <img src={logosmix} alt="Powered by Shopify & Razorpay" />
        </div>
      </div>

      <BottomNav
        tab={tab}
        setTab={navigateTab}
        cartCount={getItemCount()}
        setActiveCategory={setActiveCategory}
      />
    </Container>
      )}
    </>
  );
}

function BottomNav({ tab, setTab, cartCount }) {
  return (
    <Nav>
      <button className={tab === 'home' ? 'nav--active' : ''} onClick={() => setTab('home')}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9"/><path d="M5 10v10h5v-6h4v6h5V10"/></svg>
        <span>Home</span>
      </button>
      <button className={tab === 'categories' ? 'nav--active' : ''} onClick={() => setTab('categories')}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
        <span>Categories</span>
      </button>
      <button className={`${tab === 'cart' ? 'nav--active' : ''} ${cartCount > 0 && tab !== 'cart' ? 'nav--blink' : ''}`} onClick={() => setTab('cart')}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
        <span>Cart{cartCount > 0 ? ` (${cartCount})` : ''}</span>
      </button>
    </Nav>
  );
}

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;

  .m-dialog {
    background: #2a2a2a;
    border: 1px solid #555;
    padding: 20px;
    min-width: 260px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .m-dialog__icon {
    font-size: 28px;
    color: #4caf50;
    &--error { color: #e53935; }
  }
  .m-dialog p { margin: 0; font-size: 14px; color: #ddd; }
  .m-dialog button {
    background: #f5a623;
    border: none;
    color: #000;
    padding: 10px 30px;
    font-size: 14px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
  }
`;

const Nav = styled.nav`
  display: flex;
  background: #222;
  border-top: 1px solid #333;
  flex-shrink: 0;
  button {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 8px 4px;
    background: none;
    border: none;
    color: #777;
    font-size: 10px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    .nav-icon { width: 20px; height: 20px; }
    &.nav--active { color: #f5a623; }
    &.nav--blink {
      animation: cart-blink 2s ease-in-out infinite;
    }
  }
  @keyframes cart-blink {
    0%, 100% { color: #777; }
    50% { color: #a07020; }
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Tahoma, sans-serif;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;

  /* Header */
  .m-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: linear-gradient(180deg, #2d2d2d 0%, #1a1a1a 100%);
    border-bottom: 2px solid #f5a623;
    flex-shrink: 0;
  }
  .m-header__logo {
    height: 32px;
    width: auto;
  }
  .m-header__search {
    flex: 1;
    position: relative;
    &--expanded {
      transition: width 0.2s ease;
      overflow: visible;
      z-index: 9999;
      flex: none !important;
      width: calc(100vw - 120.41px);
    }
    input {
      width: 100%;
      background: #111;
      border: 1px solid #444;
      color: #ccc;
      padding: 8px 10px;
      font-size: 14px;
      font-family: Tahoma, sans-serif;
      &:focus { border-color: #f5a623; outline: none; }
      &::placeholder { color: #555; }
    }
  }
  /* Fullscreen search overlay */
  .m-search-fulloverlay {
    position: fixed;
    top: 50px;
    left: 0; right: 0; bottom: 0;
    background: #1a1a1a;
    z-index: 9997;
    display: flex;
    flex-direction: column;
  }
  .m-search-fulloverlay__bg {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(6px) brightness(0.3);
    transform: scale(1.05);
  }
  .m-search-fulloverlay__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    position: relative;
    z-index: 1;
  }
  .m-search-fulloverlay__section {
    font-size: 10px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    margin: 8px 0;
  }
  .m-search-fulloverlay__brands {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }
  .m-search-fulloverlay__brand-tag {
    background: #2a2a2a;
    border: 1px solid #444;
    color: #ccc;
    font-size: 12px;
    font-family: Tahoma, sans-serif;
    padding: 6px 12px;
    cursor: pointer;
    font-weight: bold;
    span { color: #666; font-size: 9px; margin-left: 4px; font-weight: normal; }
    &:active { color: #f5a623; border-color: #f5a623; }
  }
  .m-search-fulloverlay__empty {
    color: #555;
    text-align: center;
    padding: 40px;
    font-size: 14px;
  }

  .m-search-close {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #999;
    font-size: 16px;
    cursor: pointer;
    z-index: 10000;
    padding: 4px 8px;
    &:hover { color: #f5a623; }
  }
  .m-search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #666;
    font-size: 14px;
    cursor: pointer;
    padding: 4px;
    z-index: 1;
  }
  .m-search-dropdown__section {
    padding: 8px 12px 4px;
    font-size: 9px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    border-bottom: 1px solid #2a2a2a;
  }
  .m-search-dropdown__item--brand {
    font-weight: bold;
  }
  .m-search-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #222;
    border: 1px solid #444;
    z-index: 20;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .m-search-dropdown__item {
    padding: 10px 12px;
    font-size: 13px;
    color: #ccc;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #2a2a2a;
    &:last-child { border-bottom: none; }
    &:active { background: rgba(245, 166, 35, 0.08); color: #f5a623; }
  }
  .m-search-dropdown__cat {
    font-size: 9px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .m-header__viewbtn {
    background: #252525;
    border: 1px solid #444;
    color: #666;
    font-size: 16px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &.m-active { color: #f5a623; border-color: #f5a623; }
  }
  .m-header__filterbtn {
    position: relative;
    background: #252525;
    border: 1px solid #444;
    color: #666;
    width: 32px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &.m-active { color: #f5a623; border-color: #f5a623; }
  }
  .m-header__filterbadge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    border-radius: 8px;
    background: #f5a623;
    color: #1a1a1a;
    font-size: 9px;
    font-weight: bold;
    line-height: 15px;
    text-align: center;
  }

  /* Filters panel */
  .m-filters-backdrop {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
  }
  .m-filters-panel {
    margin-top: 50px;
    width: 270px;
    max-width: 88vw;
    max-height: calc(100dvh - 70px);
    display: flex;
    flex-direction: column;
    background: #1f1f1f;
    border: 1px solid #555;
    border-top: 2px solid #f5a623;
    box-shadow: -4px 6px 16px rgba(0, 0, 0, 0.6);
  }
  .m-filters__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid #333;
    flex-shrink: 0;
  }
  .m-filters__title {
    color: #f5a623;
    font-size: 13px;
    font-weight: bold;
  }
  .m-filters__done {
    background: #333;
    border: 1px solid #555;
    color: #ccc;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    padding: 5px 12px;
    cursor: pointer;
    &:active { color: #f5a623; border-color: #f5a623; }
  }
  .m-filters__section {
    padding: 10px 12px;
    border-bottom: 1px solid #2a2a2a;
    overflow-y: auto;
  }
  .m-filters__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #888;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 8px;
  }
  .m-filters__clear {
    background: transparent;
    border: none;
    color: #f5a623;
    font-size: 10px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    text-transform: none;
    letter-spacing: 0;
  }
  .m-filters__sizes {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .m-filters__chip {
    min-width: 34px;
    padding: 7px 8px;
    background: #252525;
    border: 1px solid #444;
    color: #ccc;
    font-size: 12px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    &--active {
      background: #f5a623;
      border-color: #f5a623;
      color: #1a1a1a;
      font-weight: bold;
    }
  }
  .m-filters__colors {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .m-filters__color {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 6px;
    background: transparent;
    border: none;
    color: #ccc;
    font-size: 13px;
    font-family: Tahoma, sans-serif;
    text-align: left;
    cursor: pointer;
    &:active { background: #2c2c2c; }
    &--active { color: #f5a623; }
  }
  .m-filters__swatch {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 3px;
  }
  .m-filters__colorname {
    flex: 1;
    text-transform: capitalize;
  }
  .m-filters__check {
    color: #f5a623;
    font-size: 12px;
  }

  /* Category header */
  .m-cat-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: #222;
    border-bottom: 1px solid #333;
    flex-shrink: 0;
    button {
      background: none;
      border: none;
      color: #f5a623;
      font-size: 13px;
      font-family: Tahoma, sans-serif;
      cursor: pointer;
    }
    span { color: #e0e0e0; font-weight: bold; font-size: 14px; }
    .m-cat-count { color: #666; font-size: 12px; font-weight: normal; }
  }

  /* Content */
  .m-content {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
    background-color: #1a1a1a;
    background-size: auto 100%;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    &::before {
      content: '';
      position: sticky;
      top: 0;
      display: block;
      width: 100%;
      height: 0;
      box-shadow: 0 0 0 100vmax rgba(26,26,26,0.85);
      pointer-events: none;
      z-index: 0;
    }
    &::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background-image: url(${overlaygrit});
      background-size: cover;
      background-position: center;
      mix-blend-mode: screen;
      opacity: 0.05;
      pointer-events: none;
      z-index: 0;
    }
    & > * { position: relative; z-index: 1; }
  }

  /* Payment badges */
  .m-footer-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 12px;
    background: #1a1a1a;
    flex-shrink: 0;
  }
  .m-footer-strip__links {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .m-footer-strip__email {
    font-size: 11px;
    color: #777;
    text-decoration: none;
  }
  .m-footer-strip__sep {
    color: #444;
    font-size: 11px;
  }
  .m-footer-strip__icon {
    color: #777;
    display: inline-flex;
    align-items: center;
    padding: 0 2px;
    text-decoration: none;
    &:active { color: #f5a623; }
    &[data-copied]::after {
      content: 'Copied!';
      font-size: 10px;
      margin-left: 4px;
      color: #f5a623;
    }
  }
  .m-payment-badges {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 14px;
    padding: 4px 0;
    background: #1a1a1a;
    flex-shrink: 0;
    opacity: 0.15;
    img {
      height: 18px;
      width: auto;
      filter: invert(1) brightness(2);
    }
  }

  /* Carousel */
  .m-carousel {
    overflow: hidden;
    margin: 6px 0 10px;
    border-top: 1px solid #2a2a2a;
    border-bottom: 1px solid #2a2a2a;
  }
  .m-carousel__track {
    display: flex;
    will-change: transform;
  }
  .m-carousel__slide {
    flex-shrink: 0;
    height: 160px;
    opacity: 0.7;
    img {
      height: 100%;
      width: auto;
      display: block;
    }
  }

  /* Featured Slideshow */
  .m-featured-slide {
    overflow: hidden;
    margin-bottom: 8px;
    position: relative;
  }
  .m-featured-slide__arrow {
    position: absolute;
    top: calc(50% - 20px);
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid #555;
    color: #ccc;
    font-size: 12px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 5;
    line-height: 1;
    &:active { color: #f5a623; border-color: #f5a623; }
    &--left { left: 2px; }
    &--right { right: 2px; }
  }
  .m-featured-slide__slider {
    display: flex;
    width: 200%;
    &--slide-left {
      transition: transform 0.4s ease-in-out;
      transform: translateX(-50%);
    }
    &--slide-right {
      transition: transform 0.4s ease-in-out;
      transform: translateX(0%);
    }
  }
  .m-featured-slide__page {
    width: 50%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    flex-shrink: 0;
  }
  .m-featured-slide__indicators {
    display: flex;
    justify-content: center;
    gap: 4px;
    margin-top: 10px;
  }
  .m-featured-slide__dash {
    width: 30px;
    height: 3px;
    background: #333;
    cursor: pointer;
    overflow: hidden;
    border-radius: 1px;
  }
  .m-featured-slide__dash-fill {
    height: 100%;
    background: #f5a623;
    transition: width 0.05s linear;
  }

  .m-section-divider {
    height: 1px;
    background: linear-gradient(to right, transparent, #f5a623, transparent);
    margin: 16px 0 8px;
    opacity: 0.4;
  }

  .m-banner {
    text-align: center;
    color: #f5a623;
    font-size: 12px;
    font-weight: bold;
    padding: 8px;
    margin-bottom: 8px;
  }

  /* Product grid */
  .m-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  .m-card {
    cursor: pointer;
    overflow: hidden;
    min-width: 0;
    &:active { opacity: 0.8; }
  }
  .m-card__img {
    width: 100%;
    height: 0;
    padding-bottom: 133%;
    position: relative;
    background: #111;
    border: 1px solid #333;
    overflow: hidden;
    .m-card__img-front { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain; }
    .m-card__img-back {
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      object-fit: contain;
      opacity: 0;
    }
    &--dual .m-card__img-back {
      animation: m-dual-flip-back 6s ease-in-out infinite;
    }
    &--dual::after {
      content: '⇆ DUAL';
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(0,0,0,0.6);
      color: #f5a623;
      font-size: 8px;
      padding: 2px 5px;
      border-radius: 2px;
      letter-spacing: 0.5px;
      pointer-events: none;
      z-index: 1;
    }
  }
  @keyframes m-dual-flip-back {
    0%, 45% { opacity: 0; }
    50%, 95% { opacity: 1; }
    100% { opacity: 0; }
  }
  .m-card__name {
    font-size: 12px;
    color: #ddd;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .m-card__price {
    font-size: 11px;
    color: #f5a623;
    font-weight: bold;
  }

  /* Alpha view */
  .m-alpha__label {
    font-size: 16px;
    font-weight: 900;
    color: #f5a623;
    padding: 6px 0;
    border-bottom: 1px solid #333;
    margin-bottom: 6px;
    margin-top: 8px;
  }

  .m-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 12px;
  }
  .m-loading__bar {
    width: 200px;
    height: 6px;
    background: #222;
    border: 1px solid #333;
    border-radius: 2px;
    overflow: hidden;
  }
  .m-loading__fill {
    height: 100%;
    width: 40%;
    background: #f5a623;
    animation: m-load 1.2s ease-in-out infinite;
  }
  @keyframes m-load {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(350%); }
  }
  .m-loading__text {
    color: #666;
    font-size: 11px;
  }

  .m-empty {
    text-align: center;
    color: #555;
    font-size: 14px;
    padding: 40px;
  }

  /* Categories page */
  .m-categories__heading {
    padding: 14px 16px 8px;
    font-size: 11px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 1.5px;
  }
  .m-categories {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .m-cat-group {
    background: #222;
    border: 1px solid #2a2a2a;
  }
  .m-cat-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 16px;
    background: none;
    border: none;
    color: #e0e0e0;
    font-size: 15px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 1px;
    .m-cat-arrow { color: #666; font-size: 12px; }
  }
  .m-cat-expanded {
    padding: 0 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .m-cat-all {
    background: rgba(245,166,35,0.1);
    border: 1px solid #f5a623;
    color: #f5a623;
    padding: 10px;
    font-size: 13px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    text-align: center;
  }
  .m-cat-genres {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  .m-cat-genres__label {
    width: 100%;
    font-size: 10px;
    color: #6ab4ff;
    letter-spacing: 2px;
    font-weight: bold;
  }
  .m-cat-brands {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  .m-cat-sub {
    background: #2a2a2a;
    border: 1px solid #3a3a3a;
    color: #bbb;
    padding: 6px 12px;
    font-size: 12px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    &:active { color: #f5a623; border-color: #f5a623; }
  }

`;

export default MobileBinco;
